/**
 * Spec 256 — what each AI engine's collection state is, in words.
 *
 * The readable copy that lived in the degradation banner (specs 134, 220, 253)
 * moved here when the engine strip absorbed the banner: the plain-language
 * line per failure class, the provider's JSON reply made readable, and the fix
 * for each state (a billing link, or the admin "Turn back on" action). The
 * strip renders it; nothing the banner said is lost.
 */
import type { EngineDegradation, EngineDegradationDetail } from '../types/index.js';

/** The five engines collection runs, in the strip's order. */
export const STRIP_ENGINES = ['chatgpt', 'claude', 'gemini', 'google_aio', 'mistral'] as const;
export type StripEngine = (typeof STRIP_ENGINES)[number];

const ENGINE_NAMES: Record<string, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  google_aio: 'Google AI Overview',
  mistral: 'Mistral',
};

export function engineName(engine: string): string {
  return ENGINE_NAMES[engine] ?? engine;
}

/** Live: a live key, no failures. Sample: the mock adapter. Failing: failures in
 *  the window. Off: the provider kill switch is on. */
export type EngineStatus = 'live' | 'sample' | 'failing' | 'off';

/**
 * Plain-language impact copy for each failure class. Never surfaces the raw
 * failure_class string to the operator.
 */
export function failureMessage(failureClass: string, engine: string): string {
  const name = engineName(engine);
  switch (failureClass) {
    case 'auth_invalid_key':
      return `${name} API key is invalid or expired — collection for ${name} is paused until it's fixed.`;
    case 'provider_disabled':
      return `${name} is disabled after an invalid API key — collection is paused until it's re-enabled.`;
    case 'rate_limited':
      return `${name} is rate-limiting requests — some results may be delayed.`;
    case 'timeout':
    case 'network':
      return `${name} is slow or unreachable — some results may be missing.`;
    case 'provider_5xx':
    case 'provider_unavailable':
      return `${name} is having an outage — some results may be missing.`;
    case 'empty_response':
      return `${name} returned empty responses — some results may be incomplete.`;
    case 'unknown':
    default:
      return `${name} had an unexpected error — some results may be missing.`;
  }
}

/**
 * A provider's own error text, made readable. Providers answer with JSON
 * ('{"error":{"code":404,"message":"This model …"}}', '400 {"type":"error",…}');
 * printed verbatim that is a block of JSON an operator has to act on. The
 * readable sentence names the fix; the raw reply stays one click away.
 */
export function providerMessage(raw: string): string {
  const start = raw.indexOf('{');
  if (start < 0) return raw;
  try {
    const body = JSON.parse(raw.slice(start)) as { error?: { message?: string } | string; message?: string };
    const inner = typeof body.error === 'object' ? body.error?.message : undefined;
    return inner ?? body.message ?? raw;
  } catch {
    return raw;
  }
}

/** Where the fix for an engine's state lives. Null when there is nothing a user can do. */
export type EngineFix =
  | { kind: 'reenable' }
  | { kind: 'link'; label: string; href: string }
  | null;

export interface EngineProblem {
  /** One readable line. */
  text: string;
  fix: EngineFix;
  /** The provider's reply verbatim, shown behind a disclosure. */
  raw: string | null;
}

const BILLING: Record<string, string> = {
  claude: 'https://console.anthropic.com/settings/billing',
  chatgpt: 'https://platform.openai.com/settings/organization/billing',
};

/** One failing or switched-off engine: what is wrong, in words, and where its fix is. */
export function describeProblem(e: EngineDegradationDetail): EngineProblem {
  const name = engineName(e.engine);
  const raw = e.last_error;
  const said = raw ? providerMessage(raw) : '';
  if (e.failure_class === 'provider_disabled' || e.count === null) {
    // The server's sentence knows who switched it off and why, so it is
    // preferred over the local fallback, which assumes a bad key. The fix is
    // a button, not the instruction "re-enable it" with no way to do so.
    return { text: raw ?? failureMessage('provider_disabled', e.engine), fix: { kind: 'reenable' }, raw: null };
  }
  if (/credit balance is too low|insufficient[_ ]quota|exceeded your current quota/i.test(said)) {
    const href = BILLING[e.engine];
    return {
      text: `The provider account is out of API credits — collection for ${name} is paused until it's topped up.`,
      fix: href ? { kind: 'link', label: 'Top up credits', href } : null,
      raw,
    };
  }
  if (/no longer available|model .*(?:not found|does not exist)|is not supported for generateContent/i.test(said)) {
    return {
      text: `The provider has retired the model this app uses — ${name} returns nothing until the app is updated.`,
      fix: null,
      raw,
    };
  }
  if (raw && said !== raw) return { text: said, fix: null, raw };
  return { text: raw ?? failureMessage(e.failure_class, e.engine), fix: null, raw: null };
}

/** An engine's state, derived from the degradation payload. */
export function engineStatus(engine: string, degradation: EngineDegradation | null | undefined): EngineStatus {
  if (!degradation) return 'live';
  if ((degradation.mock_engines ?? []).includes(engine)) return 'sample';
  const row = (degradation.engines ?? []).find((r) => r.engine === engine);
  if (!row) return 'live';
  if (row.failure_class === 'provider_disabled' || row.count === null) return 'off';
  return 'failing';
}

/** The degradation row for an engine, if it has one. */
export function engineRow(
  engine: string,
  degradation: EngineDegradation | null | undefined,
): EngineDegradationDetail | undefined {
  return (degradation?.engines ?? []).find((r) => r.engine === engine);
}

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/** "3 hours ago", "yesterday", "just now". */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const seconds = Math.round((then - now) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return 'just now';
  if (abs < 3600) return RELATIVE.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return RELATIVE.format(Math.round(seconds / 3600), 'hour');
  if (abs < 86400 * 30) return RELATIVE.format(Math.round(seconds / 86400), 'day');
  if (abs < 86400 * 365) return RELATIVE.format(Math.round(seconds / (86400 * 30)), 'month');
  return RELATIVE.format(Math.round(seconds / (86400 * 365)), 'year');
}
